import React, { useEffect, useId } from 'react'

import { Spinner } from '@chakra-ui/react'
import clsx from 'clsx'

import { API_KEY } from '@core/utils/constants/constants'
import getAddressStringFromObject from '@core/utils/helpers/getAddressStringFromObject'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

import './ApplyV2AddressSelect.scss'
import GooglePlacesAutocomplete, { geocodeByPlaceId, geocodeByAddress } from 'react-google-places-autocomplete'

interface GoogleValues {
    label?: string | null | undefined
    country?: string | null | undefined
    city?: string | null | undefined
    postalCode?: number | string | null
    address1?: string | null | undefined
    address2?: string | null | undefined
}

interface Props {
    isRequired?: boolean
    label?: string
    className?: string
    id?: string | number
    name?: string
    isDisabled?: boolean
    isLoading?: boolean
    errorMessage?:
        | string
        | null
        | boolean
        | {
              country?: string | string[] | null | undefined
              city?: string | string[] | null | undefined
              postalCode?: string[] | string | null | undefined
              address1?: string | string[] | null | undefined
              address2?: string | string[] | null | undefined
          }
    isShowErrorMessage?: boolean
    onFocus?: React.FocusEventHandler<HTMLDivElement>
    onBlur?: React.FocusEventHandler<HTMLDivElement>
    // options?: Option[]
    placeholder?: string
    isSearchable?: boolean
    isClearable?: boolean
    description?: React.ReactNode
    // isMulti?: boolean
    // inputValue?: string | number | null
    value: GoogleValues | null | undefined
    onChange?: (value: GoogleValues | null | undefined) => void
    options?: Array<any>
    menuPlacement?: 'top' | 'bottom' | 'auto'
}

const baseClass = 'ApplyV2AddressSelect'

function ApplyV2AddressSelect({
    value,
    isRequired = false,
    label,
    className,
    id,
    name,
    isDisabled = false,
    isLoading = false,
    errorMessage,
    isShowErrorMessage = true,
    onChange = () => {},
    onFocus = () => {},
    onBlur = () => {},
    options = [],
    placeholder = '',
    isClearable = false,
    isSearchable = true,
    description,
    menuPlacement = 'top',
}: Props) {
    const defaultId = useId()
    const selectId = id ? id.toString() : defaultId
    const { getTitle } = useApplyV2NavTranslations()
    const [selectedValue, setSelectedValue] = React.useState<{ label: string; value: string } | null>(null)
    const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false)
    const [showError, setShowError] = React.useState<boolean>(false)
    const errorTimerRef = React.useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        if (value?.label) {
            setSelectedValue({
                label: value.label,
                value: value.label,
            })
        } else {
            setSelectedValue(null)
        }
    }, [value])

    const fullAddress = getAddressStringFromObject(value)

    useEffect(() => {
        if (errorMessage) {
            if (errorTimerRef.current) {
                clearTimeout(errorTimerRef.current)
            }

            errorTimerRef.current = setTimeout(() => {
                setShowError(true)
            }, 0)
        } else {
            setShowError(false)
            if (errorTimerRef.current) {
                clearTimeout(errorTimerRef.current)
            }
        }

        return () => {
            if (errorTimerRef.current) {
                clearTimeout(errorTimerRef.current)
            }
        }
    }, [errorMessage])

    const getErrorMessage = (error: Props['errorMessage']): string => {
        if (typeof error === 'string') return error
        if (error && typeof error === 'object') {
            const errors = []
            if (error.country) errors.push(error.country)
            if (error.city) errors.push(error.city)
            if (error.postalCode) errors.push(error.postalCode)
            if (error.address1) errors.push(error.address1)
            if (error.address2) errors.push(error.address2)
            return errors.join(', ')
        }
        return ''
    }

    const getFullAddress = async (placeId: string, label: string) => {
        if (placeId.length > 0) {
            try {
                const [address] = await geocodeByPlaceId(placeId)
                const { long_name: postalCode = '' } =
                    (address?.address_components ?? []).find((c) => c?.types?.includes('postal_code')) || {}
                const { long_name: country = '' } =
                    (address?.address_components ?? []).find((c) => c?.types?.includes('country')) || {}
                const { long_name: city = '' } =
                    (address?.address_components ?? []).find(
                        (c) =>
                            c?.types?.includes('locality') ||
                            c?.types?.includes('administrative_area_level_1') ||
                            c?.types?.includes('postal_town')
                    ) || {}
                const { long_name: addressStr = '' } =
                    address.address_components.find((c) => c.types.includes('route')) || {}
                const { long_name: street_number = '' } =
                    address.address_components.find((c) => c.types.includes('street_number')) || {}
                const fullAddress = {
                    postalCode,
                    country,
                    city,
                    address1: (addressStr + ' ' + street_number).trim(),
                    label,
                }

                onChange?.(fullAddress)
            } catch (error) {
                // console.log('ApplyV2AddressSelect, getFullAddress() error', error)
                onChange?.({
                    address1: '',
                    address2: '',
                    country: '',
                    city: '',
                    postalCode: '',
                    label: null,
                })
            }
        }
    }

    const handleChange = (result: any) => {
        setIsMenuOpen(false)
        if (result?.value !== undefined) {
            getFullAddress(result.value.place_id, result.label)
        } else {
            setSelectedValue(null)
            onChange?.(null)
        }
    }

    const handleInputChange = (inputValue: string, { action }: any) => {
        if (action === 'input-change') {
            setIsMenuOpen(inputValue.trim().length > 0)
        }
    }

    const classNamePrefix = className ? `${className}__prefix` : ''

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--${name}`]: name,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
                [`${baseClass}--error`]: errorMessage && showError,
                [`${className}--disabled`]: className && isDisabled,
                [`${className}--loading`]: className && isLoading,
                [`${className}--error`]: className && errorMessage && showError,
            })}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={name ?? selectId}>
                    {label}
                    {isRequired && '*'}
                </label>
            )}

            <GooglePlacesAutocomplete
                apiKey={API_KEY}
                selectProps={{
                    // value: value ? { label: fullAddress, value: fullAddress } : null,
                    // value: inputValue ? { label: inputValue, value: inputValue } : null,
                    value: selectedValue,
                    onChange: handleChange,
                    onInputChange: handleInputChange,
                    onBlur: (e) => {
                        setIsMenuOpen(false)
                        onBlur(e)
                    },
                    onFocus: (e) => {
                        // setIsMenuOpen(true)
                        onFocus(e)
                    },
                    isSearchable: isSearchable,
                    menuPlacement: menuPlacement,
                    // menuPosition: 'fixed',
                    // classNamePrefix: `${baseClass}__input`,
                    classNamePrefix: clsx(`${baseClass}__input`, {
                        // [`${className}__prefix`]: className,
                    }),
                    noOptionsMessage: () => `${getTitle('5815', 'No results found')}...`,
                    loadingMessage: () => `${getTitle('2935', 'Searching')}...`,
                    placeholder: placeholder || `${getTitle('1979', 'Enter address')}`,
                    isLoading: isLoading,
                    backspaceRemovesValue: true,
                    inputId: name ?? selectId,
                    // name: name,
                    name: name ?? selectId,
                    captureMenuScroll: false,
                    // menuShouldScrollIntoView: true,
                    // menuShouldBlockScroll: false,
                    menuIsOpen: isMenuOpen, // TODO ------------
                    isClearable: isClearable, // TODO --------------
                    onMenuClose: () => {
                        // console.log('CLOSE')
                    },
                    minMenuHeight: 500,
                    // maxMenuHeight: 900,
                    blurInputOnSelect: false,

                    components: {
                        DropdownIndicator: () => null,
                        LoadingIndicator: () => <Spinner color="#CBCBCD" mr={'3'} size={'xs'} />,
                        IndicatorSeparator: () => null,
                        // Menu: CustomMenu,
                        // MenuList: CustomMenuList,
                    },
                }}
            />
            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {showError && errorMessage && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__error`, {
                        [`${className}__error`]: className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__error--icon`, {
                            [`${className}__error--icon`]: className,
                        })}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_4800_38853)">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.00006 1.9334C4.64954 1.9334 1.9334 4.64954 1.9334 8.00006C1.9334 11.3506 4.64954 14.0667 8.00006 14.0667C11.3506 14.0667 14.0667 11.3506 14.0667 8.00006C14.0667 4.64954 11.3506 1.9334 8.00006 1.9334ZM0.733398 8.00006C0.733398 3.9868 3.9868 0.733398 8.00006 0.733398C12.0133 0.733398 15.2667 3.9868 15.2667 8.00006C15.2667 12.0133 12.0133 15.2667 8.00006 15.2667C3.9868 15.2667 0.733398 12.0133 0.733398 8.00006ZM8.00006 9.26673C7.66869 9.26673 7.40007 8.9981 7.40007 8.66673V4.66673C7.40007 4.33536 7.66869 4.06673 8.00006 4.06673C8.33144 4.06673 8.60006 4.33536 8.60006 4.66673V8.66673C8.60006 8.9981 8.33144 9.26673 8.00006 9.26673ZM8.00006 11.3334C8.36826 11.3334 8.66673 11.0349 8.66673 10.6667C8.66673 10.2985 8.36826 10.0001 8.00006 10.0001C7.63188 10.0001 7.3334 10.2985 7.3334 10.6667C7.3334 11.0349 7.63188 11.3334 8.00006 11.3334Z"
                                    fill="#B62626"
                                />
                            </g>
                            <defs>
                                <clipPath id="clip0_4800_38853">
                                    <rect width="16" height="16" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </div>
                    <div
                        className={clsx(`${baseClass}__error--text`, {
                            [`${className}__error--text`]: className,
                        })}
                    >
                        {getErrorMessage(errorMessage)}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2AddressSelect
